import React, { FC, ReactNode } from "react";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

import { SkeletonText } from "./Skeleton";
import styles from "./CalendarTable.module.scss";
import { displayText } from "Shared/Utils/display";

// Enough rows to fill the fixed-height body.
const SKELETON_ROWS = 6;

type ColumnMonth = {
  id: string;
  label: string;
  month: number;
};

type ColumnDate = Omit<ColumnMonth, "month"> & {
  date: number;
};

interface CalendarTableProps {
  monthColumns?: ColumnMonth[];
  dateColumns: ColumnDate[];
  data?: { [key: string]: any }[];
  /** Shows placeholder rows under the real header while data loads. */
  loading?: boolean;
  /** Shown under the header when loading finishes with no rows. */
  empty?: ReactNode;
  isYearCalendar: boolean;
  onItemClick?: (id: string) => void;
  renderCellMonth: (
    row: any,
    column: any,
    onItemClick?: (id: string) => void,
  ) => ReactNode;
  renderCellDate: (
    row: any,
    column: any,
    onItemClick?: (id: string) => void,
  ) => ReactNode;
}

const CalendarTable: FC<CalendarTableProps> = ({
  data,
  loading = false,
  empty = "Nothing to show for this date range",
  monthColumns,
  dateColumns,
  isYearCalendar,
  onItemClick,
  renderCellMonth,
  renderCellDate,
}) => {
  const columns = (isYearCalendar && monthColumns) || dateColumns;
  return (
    <div className={styles.tableContainer} aria-busy={loading}>
      <MuiTable>
        <TableHead style={{ width: "100%" }}>
          <TableRow className={styles.table__head__row}>
            {columns.map((column) => {
              return (
                <TableCell
                  className={styles.table__head__row__cell}
                  key={column.label}
                >
                  {column.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading &&
            Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <TableRow
                className={styles.table__body__row}
                key={index}
                aria-hidden="true"
              >
                <TableCell className={styles.table__body__row__cell}>
                  <div className={styles.table__body__row__cell__title}>
                    <SkeletonText width="7em" />
                  </div>
                </TableCell>
                <TableCell
                  className={styles.table__body__row__cell}
                  colSpan={columns.length - 1}
                >
                  <SkeletonText width="60%" />
                </TableCell>
              </TableRow>
            ))}
          {!loading &&
            data?.map((row: any) => (
              <TableRow
                className={styles.table__body__row}
                sx={{ marginTop: "5px" }}
                key={row.id}
              >
                {columns.map((column: ColumnDate | ColumnMonth) => {
                  return (
                    <TableCell
                      className={styles.table__body__row__cell}
                      key={column.id}
                    >
                      {column.id === "title" ? (
                        <div className={styles.table__body__row__cell__title}>
                          {displayText(row?.vehicle_name)}
                        </div>
                      ) : isYearCalendar ? (
                        renderCellMonth(row, column, onItemClick)
                      ) : (
                        renderCellDate(row, column, onItemClick)
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          {!loading && !data?.length && (
            <TableRow className={styles.table__body__row}>
              <TableCell
                className={`${styles.table__body__row__cell} ${styles.emptyCell}`}
                colSpan={columns.length}
              >
                <p className={styles.empty}>{empty}</p>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </MuiTable>
    </div>
  );
};

export default CalendarTable;
